import React, { Component } from "react";
import Color from 'color';
import './map.css';
import { Menu, Dropdown, Button } from 'antd';
import {DownOutlined} from "@ant-design/icons"

// maplibre is loaded by a script tag in index.html rather than bundled: react-scripts 2 runs
// babel over node_modules, and that transpile miscompiles maplibre v2 (colliding variable
// names in the circle painter, plus a dead web worker), so tiles never decode or draw.
// npm start/build copy the library out of node_modules into public/vendor/maplibre
// (see the copy:maplibre script), which keeps the version pinned in package.json.
const maplibregl = window.maplibregl;

// GBIF's own tile server - no API key required.
// https://tile.gbif.org/ui/ lists the available styles.
const BASE_LAYERS = [
  { id: 'gbif-classic', title: 'Classic' },
  { id: 'gbif-light', title: 'Light' },
  { id: 'gbif-dark', title: 'Dark' },
  { id: 'gbif-natural', title: 'Natural' }
];

// GBIF serves 512px tiles at @1x, and the same extent at higher resolution for @2x etc.
const pixelRatio = window.devicePixelRatio > 1 ? 2 : 1;

const baseLayerStyle = style => ({
  version: 8,
  sources: {
    basemap: {
      type: 'raster',
      tiles: [`https://tile.gbif.org/3857/omt/{z}/{x}/{y}@${pixelRatio}x.png?style=${style}`],
      tileSize: 512,
      attribution: '<a href="https://www.gbif.org/citation-guidelines">GBIF</a> | &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }
  },
  layers: [{ id: 'basemap', type: 'raster', source: 'basemap' }]
});

class Map extends Component {
  constructor(props) {
    super(props);
    this.myRef = React.createRef();
    let menu = (
      <Menu onClick={this.selectBaseLayer}>
        {BASE_LAYERS.map(layer => <Menu.Item key={layer.id}>{layer.title}</Menu.Item>)}
      </Menu>
    );
    // layers can only be added once the style is ready - style.load flips this
    this.styleReady = false;
    this.state = { selected: props.selected || {}, menu, baseLayer: 'gbif-light' };
  }

  selectBaseLayer = layer => {
    var layerId = layer.key;
    if (this.state.baseLayer !== layerId) {
      // setStyle drops the occurrence layers. diff: false forces a full style reload, so
      // style.load fires and puts the current selection back - when maplibre diffs the two
      // styles instead it applies them in place and never fires the event.
      this.styleReady = false;
      this.map.setStyle(baseLayerStyle(layerId), { diff: false });
      this.setState({baseLayer: layerId});
    }
  };

  componentDidMount() {
    this.map = new maplibregl.Map({
      container: this.myRef.current,
      style: baseLayerStyle(this.state.baseLayer),
      zoom: 0,
      center: [0, 0]
    });
    this.map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-left');
    this.map.on("style.load", x => {
      this.styleReady = true;
      this.map.setCenter([0, 0]);
      this.map.setZoom(0);
      this.addLayers(this.props.selected);
    });
  }

  componentWillUnmount() {
    this.map.remove();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selected !== this.props.selected) {
      this.updateLayers(prevProps.selected, this.props.selected);
    }
    if (prevProps.shouldRefresh !== this.props.shouldRefresh) {
      this.map.resize();
    }
  }

  updateLayers = (prev, next) => {
    this.removeLayers(prev);
    this.addLayers(next);
  }

  getLayersAsArray = (selected) => {
    return Object.keys(selected).map(x => selected[x])
    .sort((firstEl, secondEl) => { return secondEl.sortIndex - firstEl.sortIndex })
    .filter(x => x.visible);
  }

  removeLayers = prev => {
    if (!this.styleReady) return;
    prev = prev || {};
    let list = this.getLayersAsArray(prev);
    list.filter(x => x.taxonKeys && x.taxonKeys.length > 0).forEach(l => {
      let layerName = "occurrences_" + l.layerName;
      var layer = this.map.getSource(layerName);
      if (layer) {
        this.map.removeLayer(layerName);
        this.map.removeSource(layerName);
      }
    });
  }

  addLayers = selected => {
    // maplibre throws if a layer is added before the style is ready - the style.load
    // handler adds the current selection once it is
    if (!this.styleReady) return;
    selected = selected || {};
    let list = this.getLayersAsArray(selected);
    let addLayer = this.addLayer;
    list.filter(x => x.taxonKeys && x.taxonKeys.length > 0).forEach(x => {
      addLayer(x)
    });
  }

  addLayer = (layer) => {
    const layerId = "occurrences_" + layer.layerName;
    // style.load and prop updates can both ask for the same layer, so never add it twice
    if (this.map.getLayer(layerId)) return;
    const query = layer.visibleTaxonKeys.reduce((str, key) => str += `&taxonKey=${key}`, '');
    var tileString = "https://api.gbif.org/v2/map/occurrence/adhoc/{z}/{x}/{y}.mvt?style=scaled.circles&mode=GEO_CENTROID&occurrenceStatus=present&srs=EPSG%3A3857&squareSize=512&hasGeospatialIssue=false&occurrenceStatus=present" + query;
    this.map.addLayer(
      {
        id: layerId,
        type: "circle",
        source: {
          type: "vector",
          tiles: [tileString]
        },
        "source-layer": "occurrence",
        paint: {
          // make circles larger as the user zooms from z12 to z22
          "circle-radius": {
            property: "total",
            type: "interval",
            stops: [[0, 2], [10, 3], [100, 5], [1000, 8], [10000, 15]]
          },
          // color circles by ethnicity, using data-driven styles
          "circle-color": layer.color,
          // "circle-opacity": {
          //   property: "total",
          //   type: "interval",
          //   stops: [[0, 1], [10, 0.8], [100, 0.7], [1000, 0.6], [10000, 0.6]]
          // },
          "circle-stroke-color": Color(layer.color).darken(0.3).hex(),
          "circle-stroke-width": 1
        }
      }
    );

    // let map = this.map;
    // this.map.on('click', 'occurrences', function(e) {
    //   // Change the cursor style as a UI indicator.
    //   map.getCanvas().style.cursor = 'pointer';

    //   // Populate the popup and set its coordinates
    //   // based on the feature found.
    //   console.log(e.features[0].properties.species);
    //   if (e.features[0].properties.species) {
    //       new maplibregl.Popup({closeButton: false})
    //           .setLngLat(e.lngLat)
    //           .setHTML(e.features[0].properties.species + <span>tester</span>)
    //           .addTo(map);
    //   }
    // });
  }

  render() {
    return (
      <div className="mapArea">
        {/* {this.props.totalSelected > this.props.max &&
          <div style={{ zIndex: 100, position: 'absolute', background: 'tomato', color: 'white', padding: '5px 10px', margin: 10, borderRadius: 3, fontWeight: 500 }}>
            Only showing {this.props.selected.length} of {this.props.totalSelected} layers
          </div>
        } */}
        <div style={{ zIndex: 100, position: 'absolute', right: 30, top: 30}}>
          <Dropdown overlay={this.state.menu}>
            <Button>
              Baselayer <DownOutlined />
            </Button>
          </Dropdown>
        </div>
        <div ref={this.myRef} className="map" />
      </div>
    );
  }
}

export default Map;
